import apiFetch from '@wordpress/api-fetch';
// eslint-disable-next-line import/no-extraneous-dependencies
import Chart from 'chart.js/auto'; // Auto packages all features. May want to optimize this later for size.

const chartIDs = ['ton-historical', 'tonx-historical'];
const elementIDs = [
	'ton-price',
	'tonx-price',
	'ton-market-cap',
	'tonx-market-cap',
	'market-value-of-crypto-holdings',
	'discount-premium-to-etavps',
	'tonx-ev',
	'estimated-treasury-asset-value-per-share',
];
const valueFormatters = {
	'ton-price': (value) =>
		Number(value).toLocaleString('en-US', {
			style: 'currency',
			currency: 'USD',
			maximumFractionDigits: 2,
			minimumFractionDigits: 2,
		}),
	'tonx-price': (value) =>
		Number(value).toLocaleString('en-US', {
			style: 'currency',
			currency: 'USD',
			maximumFractionDigits: 2,
			minimumFractionDigits: 2,
		}),
	'ton-market-cap': (value) => {
		if (value) {
			return formatDollarAmount(Number(value), 'b', 1);
		}
		return 'N/A';
	},
	'tonx-market-cap': (value) => {
		if (value) {
			return formatDollarAmount(Number(value), 'm', 1);
		}
		return 'N/A';
	},
	'market-value-of-crypto-holdings': (value) => {
		return formatDollarAmount(Number(value), 'm', 1);
	},
	'estimated-treasury-asset-value-per-share': (value) => {
		return formatDollarAmount(Number(value), 't', 2);
	},
	'discount-premium-to-etavps': (value) => {
		return value;
	},
	'tonx-ev': (value) => {
		return formatDollarAmount(Number(value), 'm', 1);
	},
};
const disclosureStatementButton = document.querySelector(
	'.disclosure-statement button'
);

/**
 * Formats a dollar amount into a human-readable string.
 *
 * @param {number} value    - The dollar amount to format.
 * @param {string} roundTo  - The rounding option: 't' for thousands, 'm' for millions, 'b' for billions.
 * @param {number} decimals - The number of decimal places to include.
 * @return {string} The formatted dollar amount.
 */
function formatDollarAmount(value, roundTo = 't', decimals = 0) {
	if (isNaN(value) || value === null) {
		return 'N/A';
	}

	if (roundTo === 'b') {
		if (value >= 1_000_000_000) {
			return `$${(value / 1_000_000_000).toFixed(decimals)}B`;
		} else if (value >= 1_000_000) {
			return `$${(value / 1_000_000).toFixed(decimals)}M`;
		}
		return value.toLocaleString('en-US', {
			style: 'currency',
			currency: 'USD',
			maximumFractionDigits: decimals,
		});
	}

	if (roundTo === 'm') {
		if (value >= 1_000_000) {
			return `$${(value / 1_000_000).toFixed(decimals)}M`;
		}
		return value.toLocaleString('en-US', {
			style: 'currency',
			currency: 'USD',
			maximumFractionDigits: decimals,
		});
	}

	// Default: thousands
	return value.toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
		maximumFractionDigits: decimals,
	});
}

function updateValueElement(parentElement, value) {
	const valueElement = parentElement.querySelector(
		'.analytics-dashboard__box-value'
	);

	if (valueElement) {
		valueElement.innerText = value;
	}
}

function renderChart(canvasElement, data) {
	const gradientPlugin = {
		id: 'customGradient',
		afterLayout(chart) {
			const ctx = canvasElement.getContext('2d');
			const canvas = chart.canvas;
			const gradient = ctx.createLinearGradient(
				0,
				0,
				0,
				canvas.getBoundingClientRect().height
			);
			gradient.addColorStop(0, 'rgba(35, 108, 225, 1)');
			gradient.addColorStop(0.9, 'rgba(35, 108, 225, 0)');
			chart.data.datasets[0].backgroundColor = gradient;
		},
	};
	/**
	 * Calculate indices for the first day of each month to use for x-axis labels.
	 */
	const dateIndices = [];
	let lastMonth = null;
	let lastYear = null;
	data.forEach((row, i) => {
		const date = new Date(row.date + 'T00:00:00Z');
		const month = date.getUTCMonth();
		const year = date.getUTCFullYear();

		if (month !== lastMonth || year !== lastYear) {
			dateIndices.push(i);
			lastMonth = month;
			lastYear = year;
		}
	});

	new Chart(canvasElement, {
		type: 'line',
		plugins: [gradientPlugin],
		options: {
			plugins: {
				legend: {
					display: false,
				},
				tooltip: {
					displayColors: false,
				},
			},
			interaction: {
				mode: 'index',
				intersect: false,
			},
			elements: {
				point: {
					radius: 0,
				},
				line: {
					borderWidth: 1,
				},
			},
			scales: {
				x: {
					grid: {
						display: false,
					},
					ticks: {
						autoSkip: false,
						maxRotation: 0,
						minRotation: 0,
						font: {
							family: 'Inter',
							size: 14,
						},
						callback(value, index) {
							if (dateIndices.includes(index)) {
								const date = new Date(
									this.getLabelForValue(value) + 'T00:00:00Z'
								);
								return date.toLocaleDateString('default', {
									month: 'short',
									timeZone: 'UTC',
								});
							}
							return '';
						},
					},
				},
				y: {
					grid: {
						display: false,
					},
					ticks: {
						font: {
							family: 'Inter',
							size: 14,
						},
					},
				},
			},
		},
		data: {
			labels: data.map((row) => row.date),
			datasets: [
				{
					borderColor: 'rgba(35, 108, 225, 1)',
					//backgroundColor: gradient,
					fill: true,
					data: data.map((row) => row.close),
				},
			],
		},
	});
}

function fetchAnalyticsData(retries = 2) {
	apiFetch({ path: '/ton-analytics/v1/data' })
		.then((data) => {
			if (!data || Object.keys(data).length === 0) {
				if (retries > 0) {
					// Retry fetching data
					fetchAnalyticsData(retries - 1);
				} else {
					// Console log error.
					console.error('No analytics data available after retries.'); // eslint-disable-line no-console
				}
				return;
			}

			chartIDs.forEach((id) => {
				const canvasElement = document.getElementById(id);
				const chartData = data[id];

				if (!canvasElement || !chartData) {
					return;
				}

				const filteredData = chartData.map((entry) => {
					return {
						date: entry.datetime,
						close: entry.close,
					};
				});

				// Update chart element.
				renderChart(canvasElement, filteredData);
			});

			elementIDs.forEach((id) => {
				const element = document.getElementById(id);
				const value = data[id];

				// Update value element.
				if (element && value && valueFormatters[id]) {
					updateValueElement(element, valueFormatters[id](value));
				}
			});
		})
		.catch((error) => {
			if (retries > 0) {
				// Retry fetching data
				fetchAnalyticsData(retries - 1);
			} else {
				console.error(
					'Error fetching analytics data after retries:',
					error
				);
			}
		});
}

fetchAnalyticsData(2);

disclosureStatementButton?.addEventListener('click', () => {
	const parentElement = disclosureStatementButton.parentElement;
	parentElement?.classList.toggle('active');
});
